import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChevronRight, ChevronLeft, Loader2, CheckCircle2, Lock, AlertCircle } from 'lucide-react';
import { UdvegaIcon } from '../components/UdvegaLogo';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Survey questions
const WIS_QUESTIONS = [
  { q: "Put you down or acted condescendingly towards you.", ctx: "Like someone laughing at your point in a meeting or saying 'What do you know?'" },
  { q: "Paid little attention to your statement or showed little interest in your opinion.", ctx: "You shared an idea but everyone acted like you said nothing." },
  { q: "Made demeaning or derogatory remarks about you.", ctx: "Someone made a nasty comment about your work or personality." },
  { q: "Addressed you in unprofessional terms, either publicly or privately.", ctx: "Someone spoke to you disrespectfully — e.g., yelling or name-calling." },
  { q: "Ignored or excluded you from professional camaraderie.", ctx: "Everyone went to lunch or formed a project group but didn't include you." },
  { q: "Doubted your judgment on a matter over which you have responsibility.", ctx: "Your work is your domain, but someone kept checking as if you're incompetent." },
  { q: "Made unwanted attempts to draw you into a discussion of personal matters.", ctx: "Someone kept pushing to know about your personal life when you didn't want to share." },
];

const EES_QUESTIONS = [
  { q: "I feel emotionally drained from my work.", ctx: "By end of day, you feel like there's zero energy left in your mind." },
  { q: "I feel used up at the end of the workday.", ctx: "Coming home from work, you just want to crash — no energy for anything else." },
  { q: "I feel fatigued when I get up in the morning and have to face another day on the job.", ctx: "Morning alarm goes off and you feel irritated and exhausted about going to work." },
  { q: "Working all day is really a strain for me.", ctx: "8 hours of work feels like climbing a mountain every single day." },
  { q: "I feel burned out from my work.", ctx: "You've completely lost interest in your job and feel irritated all the time." },
  { q: "I feel frustrated by my job.", ctx: "Small things at work make you angry and you think 'Why am I even here?'" },
  { q: "I feel I'm working too hard on my job.", ctx: "You feel like you're putting in way too much effort with nothing in return." },
  { q: "Working with people all day is really a strain for me.", ctx: "Talking to colleagues or clients feels mentally exhausting now." },
  { q: "I feel like I'm at the end of my rope.", ctx: "You feel like you can't take it anymore — resignation feels like the only way out." },
];

const API_QUESTIONS = [
  { q: "I handle unpredictable work situations easily.", ctx: "When a surprise task drops, you don't panic." },
  { q: "I adjust to new equipment or software quickly.", ctx: "New system at office? You pick it up fast." },
  { q: "I learn new tasks and technologies without much stress.", ctx: "Training sessions don't stress you out." },
  { q: "I handle work-related pressure calmly.", ctx: "Even when deadlines are tight, you keep your cool." },
  { q: "I adjust my goals when priorities change.", ctx: "Manager says 'Do this instead' — you shift immediately." },
  { q: "I listen to and consider others' viewpoints.", ctx: "You hear people out even if they disagree with you." },
  { q: "I handle emergencies effectively.", ctx: "Big mistake happens? You find a solution instead of panicking." },
  { q: "I work effectively with people from diverse backgrounds.", ctx: "Working with people from different cultures or cities is no problem." },
  { q: "I remain open to new ways of doing things.", ctx: "You've let go of 'We've always done it this way' thinking." },
  { q: "I maintain focus despite interruptions.", ctx: "Noise or meetings don't stop you from finishing your work." },
  { q: "I keep updated with professional developments.", ctx: "You stay current with what's new in your field." },
  { q: "I adapt well to changes in organizational structure.", ctx: "New boss or new team? You adjust fine." },
  { q: "I stay positive during difficult transitions.", ctx: "During tough times, you still encourage the team." },
  { q: "I quickly learn new work processes.", ctx: "New rules or systems? You understand them fast." },
  { q: "I collaborate with others to solve problems.", ctx: "You don't go solo — you work with the team." },
  { q: "I handle ambiguous situations well.", ctx: "When it's unclear what to do, you still find a way." },
  { q: "I remain flexible in shifting work priorities.", ctx: "Managing 2-3 tasks at once doesn't faze you." },
  { q: "I seek feedback to improve my performance.", ctx: "You actively ask 'How can I do better?'" },
  { q: "I adjust my behavior based on cultural context.", ctx: "You adapt your style depending on who you're working with." },
  { q: "I develop new skills to meet changing demands.", ctx: "You keep learning to stay ahead in your career." },
];

const SECTIONS = [
  { title: 'Workplace Dynamics', subtitle: 'How do people in your team treat you?', questions: WIS_QUESTIONS, scale: 7, labels: ['Never', 'Rarely', 'Occasionally', 'Sometimes', 'Often', 'Very Often', 'Daily'] },
  { title: 'Energy & Exhaustion', subtitle: 'How are you feeling inside?', questions: EES_QUESTIONS, scale: 5, labels: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'] },
  { title: 'Adaptability', subtitle: 'How do you handle change?', questions: API_QUESTIONS, scale: 5, labels: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'] },
];

export default function Assessment() {
  const { reviewId } = useParams();
  const [phase, setPhase] = useState('verify'); // verify, survey, complete, error
  const [email, setEmail] = useState('');
  const [employeeData, setEmployeeData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Survey state
  const [sectionIdx, setSectionIdx] = useState(0);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [responses, setResponses] = useState({ wis: Array(7).fill(0), ees: Array(9).fill(0), api: Array(20).fill(0) });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const totalQuestions = 36;
  const currentGlobalIdx = (sectionIdx === 0 ? questionIdx : sectionIdx === 1 ? 7 + questionIdx : 16 + questionIdx);
  const progress = ((currentGlobalIdx) / totalQuestions) * 100;

  // Auto-verify via token on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token && reviewId) {
      setVerifying(true);
      axios.post(`${API}/survey/verify-token`, { token, review_id: reviewId })
        .then(({ data }) => {
          setEmployeeData(data);
          setEmail(data.work_email);
          setPhase('survey');
        })
        .catch((err) => {
          setErrorMsg(err.response?.data?.detail || 'Invalid or expired link');
        })
        .finally(() => setVerifying(false));
    }
  }, [reviewId]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setErrorMsg('');
    try {
      const { data } = await axios.post(`${API}/survey/verify`, { work_email: email, review_id: reviewId });
      setEmployeeData(data);
      setPhase('survey');
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Verification failed');
    }
    setVerifying(false);
  };

  const handleAnswer = (value) => {
    const section = SECTIONS[sectionIdx];
    const key = sectionIdx === 0 ? 'wis' : sectionIdx === 1 ? 'ees' : 'api';
    const offset = sectionIdx === 0 ? 0 : sectionIdx === 1 ? 0 : 0;
    const newResponses = { ...responses };
    newResponses[key] = [...newResponses[key]];
    newResponses[key][questionIdx] = value;
    setResponses(newResponses);

    // Auto-advance
    setTimeout(() => {
      if (questionIdx < section.questions.length - 1) {
        setQuestionIdx(questionIdx + 1);
      } else if (sectionIdx < SECTIONS.length - 1) {
        setSectionIdx(sectionIdx + 1);
        setQuestionIdx(0);
      } else {
        handleSubmit(newResponses);
      }
    }, 300);
  };

  const handleBack = () => {
    if (questionIdx > 0) {
      setQuestionIdx(questionIdx - 1);
    } else if (sectionIdx > 0) {
      const prevSection = SECTIONS[sectionIdx - 1];
      setSectionIdx(sectionIdx - 1);
      setQuestionIdx(prevSection.questions.length - 1);
    }
  };

  const handleSubmit = async (finalResponses) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/survey/submit`, {
        review_id: reviewId,
        work_email: email,
        wis_responses: finalResponses.wis,
        ees_responses: finalResponses.ees,
        api_responses: finalResponses.api,
      });
      setResult(data);
      setPhase('complete');
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Submission failed');
      setPhase('error');
    }
    setSubmitting(false);
  };

  // Floating bubbles background
  const BubblesBg = () => (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full opacity-[0.06]"
          style={{
            width: 60 + Math.random() * 120,
            height: 60 + Math.random() * 120,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            background: i % 2 === 0 ? '#0D9488' : '#1E293B',
          }}
          animate={{
            y: [0, -30 - Math.random() * 40, 0],
            x: [0, 10 + Math.random() * 20, 0],
          }}
          transition={{ duration: 6 + Math.random() * 4, repeat: Infinity, ease: 'easeInOut', delay: i * 0.5 }}
        />
      ))}
    </div>
  );

  // Verification Phase
  if (phase === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 relative" data-testid="assessment-verify">
        <BubblesBg />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-md mx-4">
          <div className="glass-card rounded-3xl p-8 md:p-10">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl gradient-teal flex items-center justify-center">
                <UdvegaIcon size={24} />
              </div>
              <span className="font-heading font-semibold text-xl text-slate-800">Udvega</span>
            </div>
            <h2 className="font-heading text-2xl font-medium text-slate-800 text-center mb-2">Resilience Review</h2>
            <p className="text-sm text-slate-500 text-center mb-6">Your responses are 100% anonymous. Management will only see aggregated team-level data.</p>
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Work Email</label>
                <Input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.name@company.com" required
                  className="border-slate-200 focus:ring-teal-500/30 focus:border-teal-500"
                  data-testid="verify-email-input"
                />
              </div>
              {errorMsg && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                  <p className="text-sm text-red-600">{errorMsg}</p>
                </div>
              )}
              <Button type="submit" disabled={verifying} className="w-full bg-teal-600 hover:bg-teal-700 text-white h-11" data-testid="verify-submit-btn">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Assessment'}
              </Button>
            </form>
            <div className="mt-6 p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-3.5 h-3.5 text-teal-600" />
                <span className="text-xs font-semibold text-slate-600">Privacy Guarantee</span>
              </div>
              <p className="text-xs text-slate-400">Your name and email are used only for access verification. They are permanently separated from your responses before any analysis.</p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Survey Phase
  if (phase === 'survey') {
    const section = SECTIONS[sectionIdx];
    const question = section.questions[questionIdx];
    const currentKey = sectionIdx === 0 ? 'wis' : sectionIdx === 1 ? 'ees' : 'api';
    const currentAnswer = responses[currentKey][questionIdx];

    if (submitting) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 relative" data-testid="assessment-submitting">
          <BubblesBg />
          <div className="relative z-10 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
            <p className="font-heading text-xl text-slate-700">Processing your responses...</p>
            <p className="text-sm text-slate-400 mt-2">Our AI model is analyzing patterns</p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50 relative" data-testid="assessment-survey">
        <BubblesBg />
        {/* Progress Bar */}
        <div className="fixed top-0 left-0 right-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200">
          <div className="h-1 bg-slate-100">
            <motion.div className="h-full gradient-teal" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
          </div>
          <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UdvegaIcon size={20} />
              <span className="font-heading font-medium text-sm text-slate-700">Udvega</span>
            </div>
            <div className="text-right">
              <span className="text-xs text-slate-400">Section {sectionIdx + 1}/3</span>
              <span className="text-xs text-slate-300 mx-2">|</span>
              <span className="text-xs text-slate-400">{currentGlobalIdx + 1} of {totalQuestions}</span>
            </div>
          </div>
        </div>

        {/* Question */}
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4 pt-16">
          <div className="w-full max-w-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${sectionIdx}-${questionIdx}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.25 }}
                className="space-y-8"
              >
                {/* Section Label */}
                <div className="text-center">
                  <span className="text-xs font-semibold tracking-[0.1em] uppercase text-teal-600">{section.title}</span>
                  <p className="text-sm text-slate-500 mt-1.5">{section.subtitle}</p>
                </div>

                {/* Question */}
                <div className="text-center">
                  <h2 className="font-heading text-2xl md:text-3xl font-medium text-slate-800 leading-snug mb-4">
                    {question.q}
                  </h2>
                  <div className="inline-flex items-start gap-2 px-4 py-3 rounded-xl bg-teal-50/80 border border-teal-100/80 max-w-lg mx-auto text-left">
                    <span className="text-sm text-slate-600 leading-relaxed">{question.ctx}</span>
                  </div>
                </div>

                {/* Scale Options */}
                <div className="flex flex-wrap justify-center gap-2">
                  {section.labels.map((label, i) => {
                    const value = i + 1;
                    const selected = currentAnswer === value;
                    return (
                      <button
                        key={i}
                        onClick={() => handleAnswer(value)}
                        data-testid={`answer-${value}`}
                        className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                          selected
                            ? 'bg-teal-600 text-white shadow-md shadow-teal-600/20 scale-105'
                            : 'bg-white border border-slate-200 text-slate-600 hover:border-teal-400 hover:bg-teal-50'
                        }`}
                      >
                        <span className={`block text-sm mb-0.5 ${selected ? 'opacity-80' : 'text-slate-400'}`}>{value}</span>
                        {label}
                      </button>
                    );
                  })}
                </div>

                {/* Navigation */}
                <div className="flex justify-center gap-3 pt-4">
                  {(questionIdx > 0 || sectionIdx > 0) && (
                    <Button variant="ghost" onClick={handleBack} size="sm" className="text-slate-400" data-testid="prev-question-btn">
                      <ChevronLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    );
  }

  // Complete Phase
  if (phase === 'complete') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 relative" data-testid="assessment-complete">
        <BubblesBg />
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="relative z-10 w-full max-w-md mx-4">
          <div className="glass-card rounded-3xl p-10 text-center">
            <div className="w-16 h-16 rounded-2xl gradient-teal flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading text-2xl font-medium text-slate-800 mb-3">Thank You!</h2>
            <p className="text-sm text-slate-500 leading-relaxed mb-6">
              Your Resilience Review has been submitted. Your responses are 100% anonymous — your identity has been permanently separated from your answers.
            </p>
            <div className="p-4 rounded-2xl bg-teal-50 border border-teal-100">
              <Lock className="w-5 h-5 text-teal-600 mx-auto mb-2" />
              <p className="text-xs text-teal-700">Only aggregated team-level insights will be shared with your organization. No individual can ever be identified.</p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error Phase
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 relative" data-testid="assessment-error">
      <BubblesBg />
      <div className="relative z-10 text-center max-w-md mx-4">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="font-heading text-xl font-medium text-slate-800 mb-2">Something Went Wrong</h2>
        <p className="text-sm text-slate-500">{errorMsg || 'Please try again later.'}</p>
      </div>
    </div>
  );
}
